import React, { useState, useEffect, useMemo } from "react";
import { Ptag, OverlayLoader, IfComponent, Chip } from "components/core";
import { formatDateSequence, newFormatRupees, roleIsAdmin } from "utlis/utils";
import { TopHeader, Icon, TopHeaderNumber, EnrollmentTopHeader } from "../../GmcPaymentSummary/styles";
import EditIcon from "assets/svgs/edit.svg";
import { Link, useNavigate } from "react-router-dom";
import AddIcon from "@mui/icons-material/Add";
import RemoveIcon from "@mui/icons-material/Remove";
import { capitalize } from "lodash";
import {
  Button,
  Table as MuiTable,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
} from "@mui/material";
import EmptyDataLottie from "components/EmptyDataLottie";
import BaseNewDropDown from "components/core/BaseNewDropDown";
import NewDateStandardInput from "components/UniversalComponents/NewDateStandardInput";
import filterByDateRangeAndProducts from "pages/GmcPaymentSummary/utils";
import PaymentDocumentModal from "pages/GmcPaymentSummary/PaymentDocumentModal";
import { ENROLMENT_HISTORY_COLUMNS } from "constants";
import { FilterContainer, FilterSubContainer } from "./styles";
import useFetchEnrolment from "./useFetchEnrolment";
import useUser from "hooks/useUser";
import { Box } from "@mui/material";
import { useLocation } from "react-router";

const EnrolmentHistory = () => {
  const [overallData, setOverallData] = useState();
  const [tableData, setTableData] = useState();
  const [products, setProducts] = useState([]);
  const [dateRangeFrom, setDateRangeFrom] = useState();
  const [dateRangeTo, setDateRangeTo] = useState();
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [open, setOpen] = useState(false);
  const [documents, setDocuments] = useState([]);
  const [transactionDate, setTransactionDate] = useState();

  const location = useLocation();
  const navigate = useNavigate();
  const defaultOffset = 20;
  const params = new URLSearchParams(location.search);

  const { loading, enrolments } = useFetchEnrolment();

  const { getRole } = useUser();
  const role = getRole();

  const showEditButton = useMemo(() => {
    return role == "admin";
  }, [role]);

  const getEnrolmentColumnWidth = (title) => {
    switch (title) {
      case "Product":
        return "150px";
      case "Type":
        return "110px";
      case "Status":
        return "140px";
      case "Total Employees":
        return "140px";
      case "Total Lives":
        return "140px";
      case "Payment Date":
        return "130px";
      case "Payment Amount":
        return "150px";
      case "Endorsement Number":
        return "155px";
      case "Documents":
        return "105px";
      default:
        return showEditButton ? "60px" : "0px";
    }
  };

  const enrolmentColumns = useMemo(() => {
    return ENROLMENT_HISTORY_COLUMNS.filter((enr) => {
      console.log({ enr });
      if (role == "admin" && enr === "") return true;
      return true;
    });
  }, [role]);

  useEffect(() => {
    if (enrolments) {
      setOverallData(enrolments);
      setTableData(enrolments);
      const productArray = enrolments.reduce((acc, item) => {
        if (!acc.includes(item.product)) acc.push(item.product);
        return acc;
      }, []);
      setProducts(productArray);
    }
  }, [enrolments]);

  const currentPage = Number(params.get("start") || 1);

  const totalPages = useMemo(() => {
    const totalRecords = tableData ? tableData.length : 0;
    return Math.max(1, Math.ceil(totalRecords / defaultOffset));
  }, [tableData]);

  const shouldPaginate = useMemo(() => {
    return (tableData?.length || 0) > defaultOffset;
  }, [tableData]);

  const visibleRows = useMemo(() => {
    if (!tableData || tableData.length === 0) {
      return [];
    }

    const safeCurrentPage = Math.min(currentPage, totalPages);
    const startIndex = (safeCurrentPage - 1) * defaultOffset;
    return tableData.slice(startIndex, startIndex + defaultOffset);
  }, [currentPage, tableData, totalPages]);

  const goToPage = (page) => {
    const nextParams = new URLSearchParams(location.search);
    nextParams.set("start", String(page));
    nextParams.set("end", String(defaultOffset));
    navigate(
      {
        pathname: location.pathname,
        search: nextParams.toString(),
      },
      { replace: true }
    );
  };

  useEffect(() => {
    if (currentPage > totalPages) {
      goToPage(1);
    }
  }, [currentPage, totalPages, location.pathname]);

  const fixedHeaderContent = () => (
    <TableRow style={{ backgroundColor: "#25183e" }}>
      {enrolmentColumns.map((title, index) => (
        <TableCell
          key={index}
          variant="head"
          align="left"
          sx={{
            width: getEnrolmentColumnWidth(title),
            minWidth: getEnrolmentColumnWidth(title),
            padding: "18px 18px",
            color: "#ffffff",
            fontFamily: "Montserrat",
            fontWeight: 600,
            lineHeight: "20px",
            whiteSpace: "normal",
            borderBottom: "none",
            backgroundColor: "#25183e",
            textAlign: "left",
            "&:first-of-type": {
              borderTopLeftRadius: "8px",
            },
            "&:last-of-type": {
              borderTopRightRadius: "8px",
            },
          }}
        >
          <EnrollmentTopHeader
            style={{
              flexDirection: "column",
              justifyContent: "center",
              alignItems: "flex-start",
            }}
          >
            <Ptag
              style={{
                whiteSpace: ["Total Employees", "Total Lives"].includes(title)
                  ? "nowrap"
                  : "",
              }}
              fontSize="14px"
              textAlign="left"
              color="rgba(255, 255, 255, 1)"
              fontWeight="600"
              fontFamily="Montserrat"
              lineHeight="20px"
            >
              {title}
            </Ptag>
            {["Total Employees", "Total Lives"].includes(title) && (
              <Ptag
                style={{ whiteSpace: "nowrap" }}
                fontFamily="Montserrat"
                fontSize="11px"
                textAlign="left"
                color="rgba(255, 255, 255, 1)"
                fontWeight="400"
                marginTop="4px"
              >
                {"(Addition / Deletion)"}
              </Ptag>
            )}
          </EnrollmentTopHeader>
        </TableCell>
      ))}
    </TableRow>
  );

  const renderEnrolmentCellContent = (title, payment_data, index, isLastRow) => {
    const columnWidth = getEnrolmentColumnWidth(title);
    const cellStyle = {
      // padding: "8px",
      width: columnWidth,
      minWidth: columnWidth,
      padding: "18px 18px",
      textAlign: "left",
      "& > div, & > span": {
        justifyContent: "flex-start",
        textAlign: "left",
      },
      borderBottom: isLastRow ? "none" : "1px solid #E4E4E4",
    };

    switch (title) {
      case "Product":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <EnrollmentTopHeader style={{ justifyContent: "flex-start", whiteSpace: "nowrap" }}>
              <Ptag
                fontFamily="Montserrat"
                fontWeight="500"
                fontSize="14px"
                lineHeight="20px"
                color="rgba(0, 0, 0, 1)"
                // marginLeft="22px"
              >
                {payment_data.product}
              </Ptag>
            </EnrollmentTopHeader>
          </TableCell>
        );
      case "Type":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <EnrollmentTopHeader>
              <Ptag
                fontWeight="500"
                fontSize="14px"
                fontFamily="Montserrat"
                lineHeight="20px"
                color="rgba(0, 0, 0, 1)"
              >
                {payment_data.type ? capitalize(payment_data.type) : "-"}
              </Ptag>
            </EnrollmentTopHeader>
          </TableCell>
        );
      case "Status":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <EnrollmentTopHeader>
              <Ptag
                fontSize="14px"
                fontFamily="Montserrat"
                lineHeight="20px"
                color="rgba(0, 0, 0, 1)"
              >
                {statusViewHandler(
                  payment_data.status,
                  payment_data.order_id,
                  payment_data.userplan_id,
                  payment_data.product
                )}
              </Ptag>
            </EnrollmentTopHeader>
          </TableCell>
        );
      case "Total Employees":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <EnrollmentTopHeader>
              {Number(payment_data.no_of_employees_added) ||
              Number(payment_data.no_of_employees_deleted) ? (
                <>
                  {renderChip(payment_data.no_of_employees_added || 0, true)}
                  {renderChip(payment_data.no_of_employees_deleted || 0)}
                </>
              ) : (
                <Ptag fontSize="16px">-</Ptag>
              )}
            </EnrollmentTopHeader>
          </TableCell>
        );
      case "Total Lives":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <TopHeaderNumber>
              {Number(payment_data.no_of_lives_added) ||
              Number(payment_data.no_of_lives_deleted) ? (
                <>
                  {renderChip(payment_data.no_of_lives_added || 0, true)}
                  {renderChip(payment_data.no_of_lives_deleted || 0)}
                </>
              ) : (
                <Ptag fontSize="16px">-</Ptag>
              )}
            </TopHeaderNumber>
          </TableCell>
        );
      case "Payment Date":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <EnrollmentTopHeader>
              <Ptag
                fontSize="14px"
                fontFamily="Montserrat"
                lineHeight="20px"
                color="rgba(0, 0, 0, 1)"
              >
                {(payment_data.transaction_date &&
                  formatDateSequence(payment_data.transaction_date)) ||
                  "-"}
              </Ptag>
            </EnrollmentTopHeader>
          </TableCell>
        );
      case "Payment Amount":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <EnrollmentTopHeader style={{ justifyContent: "flex-start" }}>
              <Ptag
                fontSize="14px"
                fontFamily="Montserrat"
                lineHeight="20px"
                color="rgba(0, 0, 0, 1)"
                textAlign="left"
              >
                {getTotalAmount(payment_data)}
              </Ptag>
            </EnrollmentTopHeader>
          </TableCell>
        );
      case "Endorsement Number":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <EnrollmentTopHeader>
              {payment_data.endorsement_number ? (
                <Tooltip
                  arrow
                  title={<Ptag color="white">{payment_data.endorsement_number}</Ptag>}
                  style={{
                    fontSize: "14px !important",
                  }}
                >
                  <Ptag
                    fontSize="14px"
                    className="truncate-hover"
                    textAlign="left"
                    fontFamily="Montserrat"
                    lineHeight="20px"
                    color="rgba(0, 0, 0, 1)"
                  >
                    {payment_data.endorsement_number || "-"}
                  </Ptag>
                </Tooltip>
              ) : (
                "-"
              )}
            </EnrollmentTopHeader>
          </TableCell>
        );
      case "Documents":
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            <EnrollmentTopHeader style={{ gap: "0.8rem", justifyContent: "flex-start" }}>
              <Ptag
                style={{
                  fontWeight: "500",
                  fontSize: "14px",
                  color: "#2354D4",
                  letterSpacing: 0,
                  cursor: "pointer",
                  fontFamily: "Montserrat",
                  textDecoration: "underline",
                  textUnderlineOffset: "2px",
                }}
                onClick={() =>
                  openModal(
                    payment_data.documents,
                    payment_data?.transaction_date,
                    payment_data?.invoice_link_list
                  )
                }
              >
                View
              </Ptag>
            </EnrollmentTopHeader>
          </TableCell>
        );
      default:
        return (
          <TableCell key={index} align="center" sx={cellStyle}>
            {showEditButton && (
              <EnrollmentTopHeader
                onClick={() => redirectToEdit(payment_data)}
                style={{ cursor: "pointer" }}
              >
                <Icon src={EditIcon} />
              </EnrollmentTopHeader>
            )}
          </TableCell>
        );
    }
  };

  const rowContent = (_index, payment_data) => {
    return (
      <>
        {enrolmentColumns.map((title, index) =>
          renderEnrolmentCellContent(
            title,
            payment_data,
            index,
            _index === visibleRows.length - 1
          )
        )}
      </>
    );
  };

  const renderChip = (number, addition) => {
    if (number <= 0) return;
    let bgColor = addition ? "#E3F9EC" : "#FEEAEA";
    let color = addition ? "#08A045" : "#EE1010";
    let symbol = addition ? <AddIcon /> : <RemoveIcon />;

    return (
      <Chip
        style={{
          marginLeft: !addition ? "1rem" : "0rem",
          borderRadius: "11px",
        }}
        text={
          <span style={{ color, display: "flex", alignItems: "center", fontFamily: "Montserrat" }}>
            {symbol} {number}
          </span>
        }
        background={bgColor}
      />
    );
  };

  const getTotalAmount = (value) => {
    var total_amount =
      (value.cgst || 0) +
      (value.igst || 0) +
      (value.sgst || 0) +
      (value.net_premium || 0);
    if(total_amount === 0) return "-";
    if(total_amount < 0) {
      const formatted = newFormatRupees(Math.abs(total_amount));
      return `- ${formatted}`;
    }
    return newFormatRupees(total_amount);
  };
  const extractFilteredData = () => {
    const filteredData = filterByDateRangeAndProducts(
      overallData,
      [dateRangeFrom, dateRangeTo],
      selectedProducts
    );
    setTableData(filteredData);
    goToPage(1);
  };
  const clearValues = () => {
    setDateRangeFrom();
    setDateRangeTo();
    setSelectedProducts([]);
    setTableData(overallData);
    goToPage(1);
  };
  const openModal = (documents, date, wellnessInvoices) => {
    let filenames = Object.values(documents).filter((link) => {
      return link !== null;
    });
    if(wellnessInvoices && wellnessInvoices.length > 0) {
      const preLinks = wellnessInvoices.map((inv) => {
        if(typeof inv == "string") {
          return inv;
        }
        return inv.link
      });
      filenames = [...filenames, ...preLinks]
    }
    setOpen(true);
    setDocuments(filenames);
    setTransactionDate(date);
  };
  const redirectToEdit = (data) => {
    navigate("/v1/enrolments/edit", { state: data });
  };

  const textColorHandler = (value) => {
    // if(value === "PENDING") {
    //   return <Ptag fontSize="14px" fontWeight="500">{capitalize(value)}</Ptag>
    // } else 
    if (value === "ACTIVATED") {
      return <Ptag fontFamily="Montserrat"  lineHeight="20px" fontSize="14px" color="green">{capitalize(value)}</Ptag>
    } else {
      return capitalize(value);
    }
  }

  const productHandler = (product) => {
    if(product === "Group Parental Cover") { // Needed! as mapping done in constants is incorrect!
      return "Parental Cover"
    } return product
  }

  const statusViewHandler = (value, orderId, userPlanId, product) => {
    if (roleIsAdmin() && value === "PENDING") {
      return (
        <Link
          to={`create?orderId=${orderId}&userPlanId=${userPlanId}&product=${productHandler(product)}`}
          style={{
            whiteSpace: "nowrap",
            fontSize: "13px",
            color: "#2354D4",
            letterSpacing: 0,
            cursor: "pointer",
            // fontFamily: "Inter",
            fontFamily: "Montserrat",  
            lineHeight: "20px",
            // color="rgba(0, 0, 0, 1)",
            textDecoration: "underline",
          }}
        >
          Upload UHID
        </Link>
      );
    } else {
      return textColorHandler(value)
    }
  };

  const renderPaymentSummery = () => {
    return (
      <div
        style={{
          position: "relative",
          display: "flex",
          flexDirection: "column",
          minHeight: "calc(100vh)",
          overflow: "visible",
        }}
      >
        <PaymentDocumentModal
          onOpen={open}
          documents={documents}
          handleOpen={setOpen}
          transactionDate={transactionDate}
        />
        {overallData && overallData.length > 0 ? (
          <IfComponent condition={tableData && tableData.length >= 0}>
            <div
              style={{
                display: "flex",
                flexDirection: "column",
                flex: "none",
              }}
            >
              <FilterContainer>
                <FilterSubContainer>
                <div>
                  <Ptag
                    fontSize="14px"
                    fontWeight="400"
                    color="rgba(0, 0, 0, 1)"
                    lineHeight="18px"
                    fontFamily="Montserrat"
                    marginBottom="-4px"
                  >
                    Products
                  </Ptag>
                  <BaseNewDropDown
                    label="Select Products"
                    options={products}
                    values={selectedProducts}
                    onChange={setSelectedProducts}
                    withCheckboxes={true}
                    labelFontFamily={"Montserrat"}
                    labelFontWeight={"500"}
                    labelColor={"rgba(0, 0, 0, 0.7)"}
                  />
                </div>

                <div>
                  <Ptag
                    fontSize="14px"
                    fontWeight="400"
                    color="rgba(0, 0, 0, 1)"
                    lineHeight="18px"
                    fontFamily="Montserrat"
                    marginBottom="-4px"
                  >
                    {/* Payment Date */}
                    {"Issuance Date"}
                  </Ptag>
                  <NewDateStandardInput
                    label="From"
                    value={dateRangeFrom}
                    onChange={setDateRangeFrom}
                    maxDate={dateRangeTo}
                    labelFontFamily={"Montserrat"}
                    labelFontWeight={"500"}
                    labelColor={"rgba(0, 0, 0, 0.7)"}
                  />
                </div>

                <div>
                  <NewDateStandardInput
                    label="To"
                    value={dateRangeTo}
                    onChange={setDateRangeTo}
                    minDate={dateRangeFrom}
                    labelFontFamily={"Montserrat"}
                    labelFontWeight={"500"}
                    labelColor={"rgba(0, 0, 0, 0.7)"}
                  />
                </div>

                </FilterSubContainer>

                <div
                  style={{
                    display: "flex",
                    gap: "8px",
                    gridColumn: "span 1",
                    justifyContent: "flex-start",
                  }}
                >
                  <Button
                    className="pagination-prev-button-css"
                    variant="contained"
                    onClick={clearValues}
                    sx={clearButtonStyles}
                  >
                    Clear
                  </Button>

                  <Button
                    className="pagination-next-button-css"
                    variant="contained"
                    onClick={extractFilteredData}
                    sx={applyButtonStyles}
                  >
                    Apply
                  </Button>
                </div>
              </FilterContainer>
              <div
                style={{
                  flex: "none",
                  backgroundColor: "#fff",
                  borderRadius: "8px",
                  overflow: "hidden",
                }}
              >
                <TableContainer sx={{ overflowX: "auto", overflowY: "visible" }}>
                  <MuiTable
                    sx={{
                      tableLayout: "fixed",
                      width: "max-content",
                      minWidth: "100%",
                      borderCollapse: "separate",
                      borderSpacing: 0,
                    }}
                  >
                    <TableHead>{fixedHeaderContent()}</TableHead>

                    {/* --- DATA ROWS OR EMPTY SPACER --- */}
                    {visibleRows && visibleRows.length === 0 ? (
                      <TableBody>
                        <TableRow>
                          {/* Blank row that matches the full height of empty space */}
                          <TableCell
                            colSpan={enrolmentColumns?.length}
                            sx={{
                              // height: "80px", // Height for empty state container space
                              border: 0,
                              padding: 0,
                            }}
                          />
                        </TableRow>
                      </TableBody>
                    ) : null}

                    <TableBody>
                      {visibleRows.map((payment_data, index) => (
                        <TableRow
                          key={payment_data.order_id || index}
                          sx={{
                            "&:focus": { outline: "none" },
                            "&:last-child td, &:last-child th": { border: "none" },
                          }}
                        >
                          {rowContent(index, payment_data)}
                        </TableRow>
                      ))}
                    </TableBody>
                  </MuiTable>
                </TableContainer>

                {/* --- SCREEN-VIEWPORT CENTERED NO DATA TEXT --- */}
                {visibleRows && visibleRows.length === 0 && (
                  <Box
                    sx={{
                      position: "sticky",
                      left: 0,
                      right: 0,
                      width: "100%",
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      padding: "24px 0",
                      pointerEvents: "none", // Prevents blocking scrolling interactions
                    }}
                  >
                    <span className="table-nodata-text-css">No Data Found</span>
                  </Box>
                )}
              </div>

              {shouldPaginate && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "center",
                    marginTop: "18px",
                  }}
                >
                  <Ptag
                    fontWeigh="500"
                    fontFamily="Montserrat"
                    color="rgba(0, 0, 0, 1)"
                    fontSize="14px"
                    lineHeight="20px"
                  >
                    {"Page"} {`${currentPage} of ${totalPages}`}
                  </Ptag>
                  <Box sx={{ display: "flex", gap: "12px" }}>
                    <button
                      style={{ opacity: currentPage > 1 ? 1 : 0.4 }}
                      disabled={currentPage <= 1}
                      className="pagination-button-css pagination-prev-button-css"
                      onClick={() => goToPage(currentPage - 1)}
                    >
                      Previous
                    </button>
                    <button
                      style={{ opacity: currentPage < totalPages ? 1 : 0.4 }}
                      disabled={currentPage >= totalPages}
                      className="pagination-button-css pagination-next-button-css"
                      onClick={() => goToPage(currentPage + 1)}
                    >
                      Next
                    </button>
                  </Box>
                </Box>
              )}

            </div>
          </IfComponent>
        ) : (
          <EmptyDataLottie text="No Data Availble" />
        )}
      </div>
    );
  };

  return <>{loading ? <OverlayLoader /> : renderPaymentSummery()}</>;
};

export default EnrolmentHistory;

const buttonStyles = {
  fontFamily: "Montserrat",
  fontSize: "14px",
  textTransform: "none",
  // border: "1px solid",
  height: "40px",
  boxShadow: "none",
  fontWeight: "500",
  lineHeight: "24px",
  borderRadius: "5px",
  border: "none",
};

const clearButtonStyles = {
  ...buttonStyles,
  backgroundColor: "white",
  color: "rgba(34, 27, 60, 1)",
  padding: "0px 36px",
  "&:hover": {
    backgroundColor: "white",
    boxShadow: "none",
  },
};

const applyButtonStyles = {
  ...buttonStyles,
  color: "rgba(228, 219, 255, 1)",
  padding: "0px 36px",
  backgroundColor: "rgba(34, 27, 60, 1)",
  "&:hover": {
    backgroundColor: "rgba(34, 27, 60, 1)",
    boxShadow: "none",
  },
};
